import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL, resolveMediaUrl } from './api';
import CustomerChatbotActions from './CustomerChatbotActions';
import './CustomerJourneys.css';

const VISITOR_STORAGE_KEY = 'beborne_customer_chatbot_visitor_id';

function getVisitorId() {
  try {
    const existing = localStorage.getItem(VISITOR_STORAGE_KEY);
    if (existing && existing.length >= 12) {
      return existing;
    }
    const next = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(VISITOR_STORAGE_KEY, next);
    return next;
  } catch (error) {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function CustomerChatbotPage() {
  const { slug } = useParams();
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const [preparation, setPreparation] = useState(null);
  const [openedPreparations, setOpenedPreparations] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const visitorId = useMemo(() => getVisitorId(), []);
  const requestScopeRef = useRef(slug);
  requestScopeRef.current = slug;
  const sendingRef = useRef(false);

  useEffect(() => {
    const updateViewportHeight = () => {
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      const top = viewport?.offsetTop || 0;
      document.documentElement.style.setProperty('--customer-chat-vh', `${height}px`);
      document.documentElement.style.setProperty('--customer-chat-top', `${top}px`);
    };
    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateViewportHeight);
    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
      window.visualViewport?.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener('scroll', updateViewportHeight);
    };
  }, []);

  useEffect(() => {
    let active = true;
    setError('');
    setConfig(null);
    setMessages([]);
    setDraft('');
    setConversationId(null);
    setPreparation(null);
    setOpenedPreparations([]);
    fetch(`${API_URL}/api/customer-chatbot/${encodeURIComponent(slug)}/config`)
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || 'Assistant indisponible.');
        }
        return payload;
      })
      .then((payload) => {
        if (!active) {
          return;
        }
        setConfig(payload);
        setMessages([
          {
            id: 'welcome',
            role: 'assistant',
            content: payload.chatbot?.welcomeMessage || 'Bonjour, je peux vous conseiller sur la carte.',
          },
        ]);
      })
      .catch((err) => {
        if (active) {
          setError(err.message || 'Assistant indisponible.');
        }
      });
    return () => {
      active = false;
    };
  }, [slug]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  useEffect(() => {
    if (!inputFocused) {
      return;
    }
    const timers = [80, 220, 420].map((delay) => setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      window.scrollTo(0, 0);
    }, delay));
    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [inputFocused]);

  const sendMessage = async (event) => {
    event.preventDefault();
    const message = draft.trim();
    if (!message || !config || sendingRef.current) {
      return;
    }
    sendingRef.current = true;
    const requestScope = slug;
    setDraft('');
    setError('');
    const userMessage = {
      id: `${Date.now()}-user`,
      role: 'user',
      content: message,
    };
    setMessages((current) => [...current, userMessage]);
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/customer-chatbot/${encodeURIComponent(slug)}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          visitorId,
          conversationId,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Réponse impossible pour le moment.');
      }
      if (requestScopeRef.current !== requestScope) return;
      setConversationId(payload.conversationId || conversationId);
      setMessages((current) => [
        ...current,
        {
          id: `${Date.now()}-assistant`,
          role: 'assistant',
          content: payload.answer || 'Je n’ai pas trouvé la réponse dans la carte.',
          actions: Array.isArray(payload.actions) ? payload.actions : [],
        },
      ]);
    } catch (err) {
      if (requestScopeRef.current !== requestScope) return;
      setDraft(message);
      setError(err.message || 'Réponse impossible pour le moment.');
      setMessages((current) => [
        ...current,
        {
          id: `${Date.now()}-error`,
          role: 'assistant',
          content: err.message || 'Réponse impossible pour le moment.',
          isError: true,
        },
      ]);
    } finally {
      sendingRef.current = false;
      setLoading(false);
    }
  };

  const theme = config?.theme || {};
  const logoUrl = resolveMediaUrl(config?.chatbot?.logoMedia || '');
  const restaurantName = config?.account?.name || 'Restaurant';
  const botName = config?.chatbot?.name || 'Assistant restaurant';
  const safeSlug = encodeURIComponent(config?.account?.slug || slug);
  const actions = [
    config?.capabilities?.ordering === true && config?.links?.order === `/c/${safeSlug}` ? { type: 'order', label: 'Commander', prepareLabel: 'Préparer ma commande', href: config.links.order } : null,
    config?.capabilities?.reservations === true && config?.links?.reservation === `/reservation/${safeSlug}` ? { type: 'reservation', label: 'Réserver une table', prepareLabel: 'Préparer ma réservation', href: config.links.reservation } : null,
  ].filter(Boolean);
  const openPreparation = (type) => {
    if (!actions.some((action) => action.type === type)) return;
    setPreparation(type); setInputFocused(false);
    setOpenedPreparations((current) => current.some((entry) => entry.slug === slug && entry.type === type) ? current : [...current, { slug, type }]);
  };

  return (
    <>
    <main
      hidden={!!preparation}
      className={`customer-chat-page ${inputFocused ? 'is-keyboard-open' : ''}`}
      style={{
        '--chat-primary': theme.primaryColor || '#0b3d3f',
        '--chat-accent': theme.accentColor || '#d9b56f',
        '--chat-text': theme.textColor || '#231f1a',
        '--chat-bg': theme.backgroundColor || '#f5efe5',
        '--chat-surface': theme.surfaceColor || '#fffaf3',
      }}
    >
      <section className="customer-chat-shell">
        <header className="customer-chat-header">
          <div className="customer-chat-logo">
            {logoUrl ? <img src={logoUrl} alt="" /> : <span>{restaurantName.slice(0, 1).toUpperCase()}</span>}
          </div>
          <div>
            <strong>{botName}</strong>
            <span>{restaurantName}</span>
          </div>
        </header>

        <div className="customer-chat-messages" aria-live="polite">
          {error && !config ? (
            <div className="customer-chat-state">{error}</div>
          ) : null}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`customer-chat-row ${message.role === 'user' ? 'is-user' : 'is-assistant'} ${message.isError ? 'is-error' : ''}`}
            >
              <div className="customer-chat-bubble">{message.content}
                {actions.filter((action) => message.actions?.some((offer) => offer.type === action.type)).map((action) => <p key={action.type}><button type="button" onClick={() => openPreparation(action.type)}>{action.prepareLabel}</button></p>)}
              </div>
            </div>
          ))}
          {loading ? (
            <div className="customer-chat-row is-assistant">
              <div className="customer-chat-bubble customer-chat-typing">Réflexion...</div>
            </div>
          ) : null}
          <div ref={messagesEndRef} />
        </div>

        {actions.length ? <nav className="customer-chat-suggestions customer-chat-actions" aria-label="Services du restaurant">
          {actions.map((action) => <React.Fragment key={action.type}><button type="button" onClick={() => openPreparation(action.type)}>{action.prepareLabel}</button><a href={action.href}>{action.label}</a></React.Fragment>)}
        </nav> : null}

        <div className="customer-chat-suggestions">
          {['Que me conseilles-tu ?', 'Plats végétariens', 'Allergènes arachides', 'Suivre ma commande'].map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => setDraft(suggestion)}
              disabled={!config || loading}
            >
              {suggestion}
            </button>
          ))}
        </div>

        <form className="customer-chat-input" onSubmit={sendMessage}>
          <input
            ref={inputRef}
            type="text"
            aria-label="Votre message à l’assistant"
            maxLength="500"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            placeholder={config ? 'Carte, commande, réservation...' : 'Assistant indisponible'}
            disabled={!config || loading}
          />
          <button type="submit" disabled={!config || loading || !draft.trim()}>
            Envoyer
          </button>
        </form>
      </section>
    </main>
    {openedPreparations.filter((entry) => entry.slug === slug && actions.some((action) => action.type === entry.type)).map((entry) => <div className="customer-chat-preparation-container" key={`${entry.slug}:${entry.type}`} hidden={preparation !== entry.type}><CustomerChatbotActions type={entry.type} onClose={() => setPreparation(null)} /></div>)}
    </>
  );
}

export default CustomerChatbotPage;
